import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserAvatarComponent } from '../../../../shared/components/user-avatar/user-avatar';
import { LangSelect } from '../../../../shared/components/lang-select/lang-select';
import { AppButton } from '../../../../features/client/components/atom/app-button/app-button';
import { CartDropdownComponent } from '../../../../shared/components/cart-dropdown/cart-dropdown.component';
import { PublicCategoriesService } from '../../../../shared/services/public-categories.service';
import { PublicCategoryDto } from '../../../../core/api/categories-api.service';
import { I18nService } from '../../../../shared/services/i18n.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../../core/services/auth.service';
import { LanguageDto } from '../../../../core/api/_dtos/language.dto';
import { AvatarInitialsComponent } from '../../../../shared/components/avatar-initials/avatar-initials.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'app-navigation',
  imports: [
    CommonModule,
    RouterLink,
    TranslatePipe,
    UserAvatarComponent,
    LangSelect,
    AppButton,
    CartDropdownComponent,
    AvatarInitialsComponent,
  ],
  templateUrl: './navigation.html',
  styleUrl: './navigation.scss'
})
export class Navigation implements OnInit {
  private translate = inject(TranslateService);
  i18nService = inject(I18nService);
  categoriesService = inject(PublicCategoriesService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  authService = inject(AuthService);
  menuItems: { label: string; routerLink: string }[] = [];
  isProductsOpen = false;
  isMobileMenuOpen = false;
  mobileMenuView: 'main' | 'country' | 'account' = 'main';
  private closeProductsTimeout?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.updateMenu();
    this.translate.onLangChange.subscribe(() => this.updateMenu());

    // Load categories
    this.categoriesService.loadCategories().subscribe();

    // Close mobile menu on navigation
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.closeMobileMenu());
  }

  /**
   * Generate URL for category with proper language prefix and slug
   */
  getCategoryUrl(category: PublicCategoryDto): string {
    const slug = category.slug || category.id;
    return this.i18nService.generateUrlWithLang(`products/${slug}`);
  }

  get productsLink(): string {
    return this.i18nService.generateUrlWithLang('products');
  }

  private updateMenu(): void {
    this.menuItems = [
      { label: this.translate.instant('landing.menu.how_it_works'), routerLink: '/' },
      { label: this.translate.instant('landing.menu.products'), routerLink: 'products' },
      { label: this.translate.instant('landing.menu.inspire'), routerLink: '/' },
      { label: 'Blog', routerLink: 'blog' }
    ];
  }

  toggleProductsMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isProductsOpen = !this.isProductsOpen;
  }

  openProductsMenu(): void {
    if (this.closeProductsTimeout) {
      clearTimeout(this.closeProductsTimeout);
      this.closeProductsTimeout = undefined;
    }
    this.isProductsOpen = true;
  }

  scheduleCloseProductsMenu(): void {
    if (this.closeProductsTimeout) {
      clearTimeout(this.closeProductsTimeout);
    }
    this.closeProductsTimeout = setTimeout(() => {
      this.isProductsOpen = false;
      this.closeProductsTimeout = undefined;
    }, 150);
  }

  closeProductsMenu(): void {
    this.isProductsOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isProductsOpen) return;
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closeProductsMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeProductsMenu();
    this.closeMobileMenu();
  }

  openMobileMenu(): void {
    this.isMobileMenuOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.mobileMenuView = 'main';
    document.body.style.overflow = '';
  }

  showCountryView(): void {
    this.mobileMenuView = 'country';
  }

  showMainView(): void {
    this.mobileMenuView = 'main';
  }

  async selectMobileLanguage(lang: LanguageDto): Promise<void> {
    this.closeMobileMenu();
    await this.i18nService.changeLanguageAndRedirect(lang.code);
  }

  navigateToClientPanel(): void {
    this.mobileMenuView = 'account';
  }

  navigateFromAccount(route: string): void {
    this.closeMobileMenu();
    this.router.navigate([route]);
  }

  onAccountLogout(): void {
    this.closeMobileMenu();
    this.authService.logout();
  }

  get accountMenuItems(): { icon: string; label: string; route: string }[] {
    return [
      { icon: 'pi pi-user', label: 'client.navigation.account_settings', route: this.i18nService.generateUrlWithLang('client/settings') },
      { icon: 'pi pi-box', label: 'client.navigation.my_orders', route: this.i18nService.generateUrlWithLang('client/orders') },
      { icon: 'pi pi-palette', label: 'client.navigation.my_projects', route: this.i18nService.generateUrlWithLang('client/projects') },
      { icon: 'pi pi-home', label: 'client.navigation.addresses', route: this.i18nService.generateUrlWithLang('client/addresses') },
    ];
  }

  getFlagUrl(lang: LanguageDto): string {
    const code = lang.flag_code || lang.code;
    return `/assets/flags/${code}.svg`;
  }

  onMobileLoginClick(): void {
    this.closeMobileMenu();
    this.authService.handleLoginDialog();
  }

  get howItWorksLink(): string {
    return this.i18nService.generateUrlWithLang('how-it-works');
  }

  get inspireLink(): string {
    return this.i18nService.generateUrlWithLang('inspire');
  }

  get blogLink(): string {
    return this.i18nService.generateUrlWithLang('blog');
  }
}
